import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Loader, FileText, Eye, X, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  allocationRequestsAPI,
  formatCurrency,
  formatDate,
} from '../../services/api';

const statusStyles = {
  Active: 'bg-green-100 text-green-800',
  Approved: 'bg-emerald-100 text-emerald-800',
  Pending: 'bg-yellow-100 text-yellow-800',
  Rejected: 'bg-red-100 text-red-800',
  Inactive: 'bg-gray-100 text-gray-700',
  Unknown: 'bg-gray-100 text-gray-700',
};

const SubInvestorLocRequests = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['custodian-sub-investor-loc'],
    queryFn: async () => {
      const response =
        await allocationRequestsAPI.getCustodianSubInvestorRequests();
      return response.data?.data || response.data || { investors: [], summary: {} };
    },
    refetchOnWindowFocus: false,
  });

  const { data: pendingChangeRequestsData = [], isPending: changeRequestsLoading } = useQuery({
    queryKey: ['custodian-pending-change-requests'],
    queryFn: async () => {
      try {
        const resp = await allocationRequestsAPI.getCustodianPendingChangeRequests();
        const payload = resp?.data?.data;
        return Array.isArray(payload?.changeRequests) ? payload.changeRequests : [];
      } catch (error) {
        console.error('Failed to fetch pending change requests', error);
        return [];
      }
    },
    staleTime: 30_000
  });

  const approveChangeRequestMutation = useMutation({
    mutationFn: (changeRequestId) => allocationRequestsAPI.approveChangeRequest(changeRequestId),
    onSuccess: () => {
      toast.success('Amount change approved successfully.');
      queryClient.invalidateQueries(['custodian-pending-change-requests']);
      queryClient.invalidateQueries(['custodian-sub-investor-loc']);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to approve change request.';
      toast.error(message);
    }
  });

  const rejectChangeRequestMutation = useMutation({
    mutationFn: (changeRequestId) => allocationRequestsAPI.rejectChangeRequest(changeRequestId),
    onSuccess: () => {
      toast.success('Amount change rejected.');
      queryClient.invalidateQueries(['custodian-pending-change-requests']);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to reject change request.';
      toast.error(message);
    }
  });

  const investors = data?.investors || [];
  const summary = data?.summary || {
    totalInvestors: 0,
    totalAmount: 0,
    totalSubmissions: 0,
  };

  const filteredInvestors = useMemo(() => {
    if (!searchTerm) return investors;
    const term = searchTerm.toLowerCase();
    return investors.filter((investor) => {
      const haystack = `${investor.userId || ''} ${investor.name || ''} ${
        investor.email || ''
      }`.toLowerCase();
      return haystack.includes(term);
    });
  }, [investors, searchTerm]);

  const handleViewHistory = (investor) => {
    setSelectedInvestor(investor);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvestor(null);
    setStatusFilter('all'); // Reset filter when closing modal
  };

  // Helper function to determine if a bond matches the filter
  const bondMatchesFilter = (bond) => {
    if (statusFilter === 'all') return true;
    
    const status = bond.locApprovalStatus || 'Pending';
    
    if (statusFilter === 'Pending') {
      // Include both Pending L1 and Pending L2 as "Pending"
      return status === 'Pending L1' || status === 'Pending L2' || status === 'Pending';
    }
    
    if (statusFilter === 'Approved') {
      return status === 'Approved by PS';
    }
    
    return true;
  };

  const renderStatusBadge = (status = 'Unknown') => {
    const style = statusStyles[status] || statusStyles.Unknown;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}
      >
        {status}
      </span>
    );
  };

  const handleExportReport = async () => {
    try {
      toast.info('Fetching all Sub Investor LOC data for export...');
      
      // Fetch all sub-investor LOC data (not paginated)
      const response = await allocationRequestsAPI.getCustodianSubInvestorRequests();
      const exportData = response.data?.data || response.data || { investors: [], summary: {} };
      const allInvestors = exportData.investors || [];

      // CSV Headers
      const headers = [
        'Sub-Investor User ID',
        'Sub-Investor Name',
        'Sub-Investor Email',
        'Account Status',
        'Bond Name',
        'Bond ID',
        'ISIN',
        'Stock Code',
        'Primary Subscriber Name',
        'LOC Approval Status',
        'Submission Date',
        'Amount',
        'Total Requested Amount (Bond)',
        'Submission Count (Bond)'
      ];

      const rows = [headers];

      // Process all investors and their bonds/submissions
      allInvestors.forEach((investor) => {
        if (investor.bonds && investor.bonds.length > 0) {
          investor.bonds.forEach((bond) => {
            if (bond.submissions && bond.submissions.length > 0) {
              // One row per submission
              bond.submissions.forEach((submission) => {
                rows.push([
                  investor.userId || 'N/A',
                  investor.name || 'N/A',
                  investor.email || 'N/A',
                  investor.accountStatus || 'N/A',
                  bond.bondName || 'N/A',
                  bond.bondId || 'N/A',
                  bond.isin || '-',
                  bond.stockCode || '-',
                  bond.primarySubscriberName || 'N/A',
                  bond.locApprovalStatus || 'Pending',
                  submission.submittedAt ? formatDate(submission.submittedAt) : 'N/A',
                  (submission.amount || 0).toString(),
                  (bond.totalAmount || 0).toString(),
                  (bond.submissionCount || 0).toString()
                ]);
              });
            } else {
              // If bond has no submissions, still add one row with bond info
              rows.push([
                investor.userId || 'N/A',
                investor.name || 'N/A',
                investor.email || 'N/A',
                investor.accountStatus || 'N/A',
                bond.bondName || 'N/A',
                bond.bondId || 'N/A',
                bond.isin || '-',
                bond.stockCode || '-',
                bond.primarySubscriberName || 'N/A',
                bond.locApprovalStatus || 'Pending',
                'N/A',
                '0',
                (bond.totalAmount || 0).toString(),
                '0'
              ]);
            }
          });
        } else {
          // If investor has no bonds, add one row with investor info only
          rows.push([
            investor.userId || 'N/A',
            investor.name || 'N/A',
            investor.email || 'N/A',
            investor.accountStatus || 'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            '0',
            '0',
            '0'
          ]);
        }
      });

      // Convert to CSV format
      const csvContent = rows
        .map(row => row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `sub_investor_loc_requests_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Report exported successfully with ${rows.length - 1} records.`);
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to generate report. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex flex-col mb-8 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Sub Investor LOC 
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Monitor LOC submissions from your managed sub-investors
              
            </p>
          </div>
          <button
            onClick={handleExportReport}
            className="btn-primary"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>

        <div className="mb-6 card">
          <div className="flex flex-col space-y-4 card-body md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="w-full md:max-w-xl">
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by user ID, name, or email..."
                  className="w-full pl-10 form-input"
                />
              </div>
            </div>
            {isFetching && (
              <div className="flex items-center text-sm text-gray-500">
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Updating data...
              </div>
            )}
          </div>
        </div>

        {/* Pending Amount Change Requests */}
        {pendingChangeRequestsData.length > 0 && (
          <div className="mb-6 border-yellow-200 card bg-yellow-50">
            <div className="card-header bg-amber-50">
              <h3 className="text-lg font-semibold text-yellow-900">
                Pending Amount Change Requests
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Facility Agent has requested to change LOC amounts for your sub-investors. Please review and approve or reject.
              </p>
            </div>
            <div className="p-0 card-body">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="bg-yellow-100 table-header">
                    <tr>
                      <th className="text-gray-700 table-header-cell">Bond Name</th>
                      <th className="text-gray-700 table-header-cell">Sub-Investor</th>
                      <th className="text-gray-700 table-header-cell">Old Amount</th>
                      <th className="text-gray-700 table-header-cell">New Amount</th>
                      <th className="text-gray-700 table-header-cell">Requested By</th>
                      <th className="text-gray-700 table-header-cell">Requested Date</th>
                      <th className="text-gray-700 table-header-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingChangeRequestsData.map((changeRequest) => {
                      const requestedByName = changeRequest.requestedBy?.corporateInfo?.companyName ||
                        [changeRequest.requestedBy?.personalInfo?.firstName, changeRequest.requestedBy?.personalInfo?.lastName]
                          .filter(Boolean)
                          .join(' ') ||
                        changeRequest.requestedBy?.email ||
                        'Unknown';
                      const investorName = changeRequest.investorId?.corporateInfo?.companyName ||
                        [changeRequest.investorId?.personalInfo?.firstName, changeRequest.investorId?.personalInfo?.lastName]
                          .filter(Boolean)
                          .join(' ') ||
                        changeRequest.investorId?.email ||
                        'Unknown';
                      return (
                        <tr key={changeRequest._id} >
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {changeRequest.bondName || 'Unknown Bond'}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {investorName}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-700">
                            {formatCurrency(changeRequest.oldAmount || 0)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-900">
                            {formatCurrency(changeRequest.newAmount || 0)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {requestedByName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {changeRequest.requestedAt ? formatDate(changeRequest.requestedAt) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => approveChangeRequestMutation.mutate(changeRequest._id)}
                                disabled={approveChangeRequestMutation.isPending || rejectChangeRequestMutation.isPending}
                                className="btn-primary btn-sm"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => rejectChangeRequestMutation.mutate(changeRequest._id)}
                                disabled={approveChangeRequestMutation.isPending || rejectChangeRequestMutation.isPending}
                                className="btn-secondary btn-sm"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-100 rounded-lg shadow">

          {isLoading ? (
            <div className="py-16 text-center">
              <Loader className="w-8 h-8 mx-auto mb-3 text-gray-400 animate-spin" />
              <p className="text-gray-600">Loading LOC requests...</p>
            </div>
          ) : filteredInvestors.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600">
                {searchTerm
                  ? 'No sub-investors match your search.'
                  : 'No LOC submissions from your sub-investors yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      User ID
                    </th>
                    <th className="table-header-cell">
                      Investor
                    </th>
                    <th className="table-header-cell">
                      Account Status
                    </th>
                    <th className="table-header-cell">
                      Latest LOC
                    </th>
                    <th className="table-header-cell">
                      Total Requested
                    </th>
                    <th className="table-header-cell">
                      Submissions
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {filteredInvestors.map((investor) => (
                    <tr key={investor.id}>
                      <td className="table-cell">
                        {investor.userId}
                      </td>
                      <td className="table-cell">
                        <div className="font-medium text-gray-900">
                          {investor.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {investor.email || '—'}
                        </div>
                      </td>
                      <td className="table-cell">
                        {renderStatusBadge(investor.accountStatus)}
                      </td>
                      <td className="table-cell">
                        {investor.latestSubmission ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {investor.latestSubmission.bondName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(investor.latestSubmission.submittedAt)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            No submissions yet
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        {formatCurrency(investor.totalRequestedAmount || 0)}
                      </td>
                      <td className="table-cell">
                        {investor.submissionCount || 0}
                      </td>
                      <td className="table-cell">
                        <button
                          className="inline-flex items-center btn-secondary btn-sm"
                          onClick={() => handleViewHistory(investor)}
                          disabled={!investor.submissionCount}
                        >
                         
                          View LOC 
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && selectedInvestor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedInvestor.name}
                </h2>
                <p className="text-sm text-gray-500">{selectedInvestor.email}</p>
                <p className="mt-1 text-xs text-gray-400">
                  User ID: {selectedInvestor.userId}
                </p>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCloseModal}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 px-6 py-4 border-b border-gray-100 md:grid-cols-3">
              <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                <p className="text-xs tracking-wide text-gray-500 uppercase">
                  Total Requested
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {formatCurrency(selectedInvestor.totalRequestedAmount || 0)}
                </p>
              </div>
              <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                <p className="text-xs tracking-wide text-gray-500 uppercase">
                  Bonds
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {selectedInvestor.bonds?.length || 0}
                </p>
              </div>
              <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                <p className="text-xs tracking-wide text-gray-500 uppercase">
                  Submissions
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {selectedInvestor.submissionCount || 0}
                </p>
              </div>
            </div>

            {/* Status Filter */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <label htmlFor="modal-status-filter" className="text-sm font-medium text-gray-700">
                  Filter by Status:
                </label>
                <select
                  id="modal-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                </select>
              </div>
            </div>

            <div className="px-6 py-6 space-y-6">
              {(() => {
                // Filter bonds based on status filter
                const filteredBonds = selectedInvestor.bonds?.filter(bondMatchesFilter) || [];
                
                return filteredBonds.length > 0 ? (
                  filteredBonds.map((bond) => (
                  <div
                    key={bond.bondId}
                    className="border border-gray-100 rounded-lg shadow-sm"
                  >
                    <div className="flex flex-col px-5 py-4 border-b border-gray-100 md:flex-row md:items-center md:justify-between bg-gray-50">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {bond.bondName}
                          </p>
                          {bond.locApprovalStatus && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              bond.locApprovalStatus === 'Approved by PS' 
                                ? 'bg-green-100 text-green-800'
                                : bond.locApprovalStatus === 'Rejected'
                                ? 'bg-red-100 text-red-800'
                                : bond.locApprovalStatus === 'Pending L2'
                                ? 'bg-orange-100 text-orange-800'
                                : bond.locApprovalStatus === 'Pending L1'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {bond.locApprovalStatus}
                            </span>
                          )}
                        </div>
                        <p className="font-mono text-xs text-gray-500">
                          Bond ID: {bond.bondId}
                        </p>
                        <div className="flex mt-1 space-x-3 text-xs text-gray-500">
                          {bond.isin && <span>ISIN: {bond.isin}</span>}
                          {bond.stockCode && <span>Stock: {bond.stockCode}</span>}
                        </div>
                        {bond.primarySubscriberName && (
                          <div className="mt-2 text-xs text-gray-600">
                            <span className="font-medium">Primary Subscriber:</span> {bond.primarySubscriberName}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 text-right md:mt-0">
                        <p className="text-xs tracking-wide text-gray-500 uppercase">
                          Total Requested
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatCurrency(bond.totalAmount || 0)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {bond.submissionCount || 0} submissions
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell">
                              Request Date
                            </th>
                            <th className="table-header-cell">
                              Amount
                            </th>
                        
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {bond.submissions.map((submission, idx) => (
                            <tr key={`${bond.bondId}-${idx}`} className="bg-white">
                              <td className="px-5 py-3 text-gray-700">
                                {submission.submittedAt
                                  ? formatDate(submission.submittedAt)
                                  : '—'}
                              </td>
                              <td className="px-5 py-3 font-medium text-gray-900">
                                {formatCurrency(submission.amount || 0)}
                              </td>
                           
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  ))
                ) : (
                  <div className="py-12 text-center">
                    <FileText className="w-10 h-10 mx-auto mb-4 text-gray-300" />
                    <p className="text-sm text-gray-500">
                      {statusFilter === 'all' 
                        ? 'No LOC submissions found for this investor.'
                        : `No bonds found with ${statusFilter === 'Pending' ? 'Pending' : 'Approved'} status.`}
                    </p>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-100">
              <button className="btn-primary" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubInvestorLocRequests;

